import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/use-toast';

export interface VentureDeal {
  id: string;
  title: string;
  description?: string;
  deal_value: number;
  deal_type: string;
  deal_category: string;
  status: string;
  priority: string;
  estimated_completion_date?: string;
  actual_completion_date?: string;
  talent_profile_id: string;
  venture_profile_id?: string;
  created_at: string;
  updated_at: string;
  talent?: {
    id: string;
    name: string;
    sport: string;
    photo?: string;
    first_name?: string;
    last_name?: string;
  };
  tasks?: DealTask[];
}

export interface DealTask {
  id: string;
  deal_id: string;
  title: string;
  description?: string;
  status: string;
  assigned_to: string;
  start_date?: string;
  due_date?: string;
  completion_date?: string;
  progress_percentage: number;
  is_milestone: boolean;
  payment_amount?: number;
  payment_status?: 'unpaid' | 'due' | 'paid';
  paid_at?: string;
  paid_by_profile_id?: string;
  payment_transaction_id?: string;
}

export const useVentureDeals = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const [deals, setDeals] = useState<VentureDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVentureDeals = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.id || currentUser.account_type !== 'venture') {
        console.log('useVentureDeals: User is not a venture capital user');
        setDeals([]);
        return;
      }

      // Get current user's profile ID
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.user_id)
        .single();

      if (!profiles) {
        setError('Profile not found');
        return;
      }

      // Fetch deals where this VC is the venture_profile_id
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('*')
        .eq('venture_profile_id', profiles.id)
        .eq('deal_category', 'investment')
        .order('created_at', { ascending: false });

      if (dealsError) {
        console.error('Error fetching venture deals:', dealsError);
        setError('Failed to fetch deals');
        return;
      }

      // Fetch athlete data for each deal
      const enrichedDeals = await Promise.all(
        (dealsData || []).map(async (deal) => {
          // Get talent data
          const { data: talentData } = await supabase
            .from('talents')
            .select('id, name, sport, photo, first_name, last_name')
            .eq('profile_id', deal.talent_profile_id)
            .single();

          // Get deal tasks
          const { data: tasksData } = await supabase
            .from('deal_tasks')
            .select('*')
            .eq('deal_id', deal.id)
            .order('due_date', { ascending: true });

          return {
            ...deal,
            talent: talentData,
            tasks: tasksData || []
          };
        })
      );

      setDeals(enrichedDeals as VentureDeal[]);
    } catch (err) {
      console.error('useVentureDeals: Error:', err);
      setError('Failed to load venture deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVentureDeals();
  }, [currentUser?.id, currentUser?.account_type]);

  const createDeal = async (dealData: Partial<VentureDeal> & { talent_profile_id: string; title: string }) => {
    try {
      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }

      // Get current user's profile ID
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.user_id)
        .single();

      if (!profiles) {
        throw new Error('Profile not found');
      }

      const { data, error } = await supabase
        .from('deals')
        .insert([{
          ...dealData,
          venture_profile_id: profiles.id,
          deal_category: 'investment'
        }])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Investment Deal Created",
        description: "Successfully created new investment deal.",
      });

      await fetchVentureDeals();
      return data;
    } catch (err) {
      console.error('Error creating venture deal:', err);
      toast({
        title: "Error",
        description: "Failed to create investment deal.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateDeal = async (dealId: string, updates: Partial<VentureDeal>) => {
    try {
      const { error } = await supabase
        .from('deals')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', dealId);

      if (error) throw error;

      toast({
        title: "Deal Updated",
        description: "Investment deal updated successfully.",
      });

      await fetchVentureDeals();
    } catch (err) {
      console.error('Error updating venture deal:', err);
      toast({
        title: "Error",
        description: "Failed to update investment deal.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const createTask = async (dealId: string, taskData: Partial<DealTask> & { title: string }) => {
    try {
      const { error } = await supabase
        .from('deal_tasks')
        .insert([{
          ...taskData,
          deal_id: dealId
        }]);

      if (error) throw error;

      toast({
        title: "Task Created",
        description: "Deal task created successfully.",
      });

      await fetchVentureDeals();
    } catch (err) {
      console.error('Error creating deal task:', err);
      toast({
        title: "Error",
        description: "Failed to create deal task.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateTask = async (taskId: string, updates: Partial<DealTask>) => {
    try {
      const { error } = await supabase
        .from('deal_tasks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) throw error;

      toast({
        title: "Task Updated",
        description: "Deal task updated successfully.",
      });

      await fetchVentureDeals();
    } catch (err) {
      console.error('Error updating deal task:', err);
      toast({
        title: "Error",
        description: "Failed to update deal task.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteDeal = async (dealId: string) => {
    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', dealId);

      if (error) throw error;

      toast({
        title: "Deal Deleted",
        description: "Investment deal deleted successfully.",
      });

      await fetchVentureDeals();
    } catch (err) {
      console.error('Error deleting venture deal:', err);
      toast({
        title: "Error",
        description: "Failed to delete investment deal.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const getMetrics = () => {
    const totalDeals = deals.length;
    const activeDeals = deals.filter(deal => deal.status === 'active').length;
    const closedDeals = deals.filter(deal => deal.status === 'closed').length;
    const totalInvestment = deals.reduce((sum, deal) => sum + (deal.deal_value || 0), 0);
    const avgDealSize = totalDeals > 0 ? totalInvestment / totalDeals : 0;

    return {
      totalDeals,
      activeDeals,
      closedDeals,
      totalInvestment,
      avgDealSize
    };
  };

  return {
    deals,
    loading,
    error,
    fetchVentureDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    createTask,
    updateTask,
    getMetrics
  };
};